/**
 * PrivacyOnboardingTooltip Component
 *
 * First-time user onboarding tooltip for the sensitive data privacy feature.
 * Shows a helpful guide pointing to the eye icon toggle button.
 * Auto-dismisses after user acknowledges or clicks elsewhere.
 *
 * @example
 * <PrivacyOnboardingTooltip />
 */

import React, { useEffect, useState } from 'react'
import { X, Eye, ShieldCheck, Monitor } from 'lucide-react'
import { usePrivacyStore } from '@/stores/privacyStore'
import { cn } from '@/lib/utils'

export const PrivacyOnboardingTooltip: React.FC = () => {
  const { hasSeenOnboarding, markOnboardingAsSeen } = usePrivacyStore()
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimatingOut, setIsAnimatingOut] = useState(false)

  useEffect(() => {
    // Show tooltip after a short delay if user hasn't seen it
    if (!hasSeenOnboarding) {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1500) // Wait 1.5s after page load

      return () => clearTimeout(timer)
    }
  }, [hasSeenOnboarding])

  const handleDismiss = () => {
    setIsAnimatingOut(true)
    setTimeout(() => {
      setIsVisible(false)
      markOnboardingAsSeen()
    }, 300) // Match animation duration
  }

  const handleGotIt = () => {
    handleDismiss()
  }

  if (!isVisible || hasSeenOnboarding) {
    return null
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300',
          isAnimatingOut ? 'opacity-0' : 'opacity-100'
        )}
        onClick={handleDismiss}
      />

      {/* Tooltip card - positioned near the toggle button */}
      <div
        className={cn(
          'fixed top-20 right-4 sm:right-8 z-[9999] w-[320px] sm:w-[380px]',
          'bg-gradient-to-br from-brand-dark-light to-brand-dark',
          'border-2 border-brand-primary/50',
          'rounded-monumenta shadow-monumenta-xl',
          'p-6 transition-all duration-300',
          isAnimatingOut
            ? 'opacity-0 scale-95 translate-y-2'
            : 'opacity-100 scale-100 translate-y-0'
        )}
        role="dialog"
        aria-labelledby="privacy-onboarding-title"
        aria-describedby="privacy-onboarding-description"
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
          aria-label="Close onboarding tooltip"
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="relative">
            <div className="absolute inset-0 bg-brand-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full p-3">
              <ShieldCheck className="w-8 h-8 text-brand-dark" />
            </div>
          </div>
        </div>

        {/* Title */}
        <h3
          id="privacy-onboarding-title"
          className="font-display text-xl font-bold text-white text-center mb-3"
        >
          Nova Funcionalidade de Privacidade!
        </h3>

        {/* Description */}
        <div
          id="privacy-onboarding-description"
          className="space-y-3 text-sm text-brand-gray font-body mb-5"
        >
          <p className="flex items-start gap-2">
            <Eye className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-primary" />
            <span>
              Clique no <strong className="text-white">ícone de olho</strong> no topo da
              página para ocultar informações sensíveis
            </span>
          </p>
          <p className="flex items-start gap-2">
            <Monitor className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-primary" />
            <span>
              Perfeito para <strong className="text-white">compartilhar tela</strong>,
              apresentações ou trabalhar em locais públicos
            </span>
          </p>
          <p className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-primary" />
            <span>
              Oculta emails, telefones, salários e outras{' '}
              <strong className="text-white">informações confidenciais</strong>
            </span>
          </p>
        </div>

        {/* Keyboard shortcut hint */}
        <div className="bg-brand-dark-lighter/50 rounded-monumenta p-3 mb-4 border border-brand-primary/20">
          <p className="text-xs text-brand-gray text-center">
            Atalho rápido:{' '}
            <kbd className="px-2 py-1 bg-brand-dark rounded text-brand-primary font-mono text-xs">
              Ctrl
            </kbd>{' '}
            +{' '}
            <kbd className="px-2 py-1 bg-brand-dark rounded text-brand-primary font-mono text-xs">
              Shift
            </kbd>{' '}
            +{' '}
            <kbd className="px-2 py-1 bg-brand-dark rounded text-brand-primary font-mono text-xs">
              H
            </kbd>
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleGotIt}
            className="flex-1 bg-gradient-to-r from-brand-primary to-brand-accent-600 text-brand-dark font-display font-semibold py-3 px-4 rounded-monumenta hover:shadow-monumenta-brand transition-all duration-200 hover:scale-105"
          >
            Entendi!
          </button>
        </div>

        {/* Arrow pointing up to the toggle button */}
        <div className="absolute -top-3 right-12 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[12px] border-b-brand-primary/50" />
      </div>
    </>
  )
}
